import { useCallback, useEffect, useRef, useState } from 'react';

import {
  citationHydrateErrorMessage,
  evidenceLocatorKey,
  hydratedFocusIds,
  isHydratableArtifactCitation,
  pickHydratedArtifactItem,
} from './citation-evidence';
import {
  hydrateEvidence,
  MOBILE_USER_FACING_ERROR_MESSAGE,
  type HydrateDisplay,
  type HydrateEvidenceLocator,
  type HydrateEvidenceWindow,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  isMobileEvidenceLocator,
  mobileViewerHrefFromHydrate,
  mobileViewerHrefFromLocator,
} from './citation-viewer-link';
import type { Source } from '@/shared/types';

type CitationLike = Pick<Source, 'sourceFilePath' | 'evidence'>;

export type HydratedArtifactStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export const HYDRATE_TABLE_PAGE_SIZE = 50;
/** Match mobile-service ``MAP_POINT_CAP`` / the standalone map point cap. */
export const HYDRATE_MAP_POINT_CAP = 10_000;

export type HydratePageRequest = {
  offset: number;
  limit?: number;
  requestedView?: string | null;
  /** Keep the rows already shown and place this page in front of them. */
  prepend?: boolean;
};

/** Older history goes in front of the open window. A table page change replaces it. */
export function mergeHydratedWindow(
  current: Record<string, unknown>[],
  incoming: Record<string, unknown>[],
  prepend: boolean,
): Record<string, unknown>[] {
  if (!prepend) return incoming;
  const seen = new Set(incoming.map(hydratedItemKey));
  return [...incoming, ...current.filter((item) => !seen.has(hydratedItemKey(item)))];
}

function hydratedItemKey(item: Record<string, unknown>): string {
  const id = item.mobileArtifactId ?? item.id;
  return id == null ? JSON.stringify(item) : String(id);
}

export type HydratedArtifactState = {
  item: Record<string, unknown> | null;
  /** Every row hydrate returned, which for a thread is the whole conversation. */
  items: Record<string, unknown>[];
  /** Which field carries the body, sender and timestamp, per the resolved view. */
  displayFields: Record<string, string> | null;
  focusIds: number[];
  highlightIds: number[];
  window: HydrateEvidenceWindow | null;
  graphqlType: string | null;
  /** Registry view hydrate resolved, so a map window still plots when display is json. */
  view: string | null;
  /** Deep-link for the pane's "Open in mobile viewer" header, not a click hijack. */
  viewerHref: string | null;
  display: HydrateDisplay | null;
  total: number;
  truncated: boolean;
  offset: number;
  /** Requested table page size. Defaults to ``HYDRATE_TABLE_PAGE_SIZE`` until the user changes it. */
  limit: number;
  status: HydratedArtifactStatus;
  errorMessage: string | null;
  setPage: (page: Partial<HydratePageRequest> & Pick<HydratePageRequest, 'offset'>) => void;
};

const NOOP_PAGE = () => {};

const IDLE: HydratedArtifactState = {
  item: null,
  items: [],
  displayFields: null,
  focusIds: [],
  highlightIds: [],
  window: null,
  graphqlType: null,
  view: null,
  viewerHref: null,
  display: null,
  total: 0,
  truncated: false,
  offset: 0,
  limit: HYDRATE_TABLE_PAGE_SIZE,
  status: 'idle',
  errorMessage: null,
  setPage: NOOP_PAGE,
};

function graphqlTypeOf(locator: unknown): string | null {
  if (!locator || typeof locator !== 'object') return null;
  const value = (locator as { graphqlType?: unknown }).graphqlType;
  return typeof value === 'string' && value ? value : null;
}

function viewerHrefOf(
  evidence: unknown,
  hydrated?: Parameters<typeof mobileViewerHrefFromHydrate>[0],
): string | null {
  if (hydrated) {
    const href = mobileViewerHrefFromHydrate(hydrated);
    if (href) return href;
  }
  return isMobileEvidenceLocator(evidence) ? mobileViewerHrefFromLocator(evidence) : null;
}

function displayFieldsOf(locator: HydrateEvidenceLocator): Record<string, string> | null {
  const fields = locator.displayFields;
  return fields && typeof fields === 'object' ? (fields as Record<string, string>) : null;
}

export function hydrateDisplay(value: unknown): HydrateDisplay {
  return value === 'markdown' || value === 'thread' || value === 'map' ? value : 'json';
}

/** True when a completed hydrate belongs to this locator, so a view or page change can keep the rows on screen. */
export function hydrateKeepsCurrentRows(readyRequestKey: string, locatorKey: string): boolean {
  return locatorKey !== '' && readyRequestKey.startsWith(`${locatorKey}:`);
}

/** False after this effect aborted, so a late hydrate must not write pane state. */
export function shouldCommitHydrate(signal: AbortSignal): boolean {
  return !signal.aborted;
}

/**
 * POST a skinny artifact citation to ``/evidence/hydrate`` and expose the cited
 * row (``focus``), not necessarily ``items[0]`` of a windowed thread.
 */
export function useHydratedArtifactEvidence(
  source: CitationLike | null | undefined,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  enabled = true,
): HydratedArtifactState {
  const [state, setState] = useState<HydratedArtifactState>(IDLE);
  const evidence = source?.evidence;
  const shouldHydrate = Boolean(
    enabled && source && isHydratableArtifactCitation(source) && evidence,
  );
  const locatorKey = shouldHydrate ? evidenceLocatorKey(evidence) : '';
  const [page, setPageState] = useState<HydratePageRequest>({ offset: 0 });
  const [pageLocator, setPageLocator] = useState(locatorKey);
  // Reset before the fetch effect runs. A locator change and a setState in an
  // effect would otherwise hydrate once at the previous offset.
  if (pageLocator !== locatorKey) {
    setPageLocator(locatorKey);
    setPageState({ offset: 0 });
  }
  const setPage = useCallback((next: Partial<HydratePageRequest> & Pick<HydratePageRequest, 'offset'>) => {
    setPageState((prev) => ({
      offset: next.offset,
      limit: next.limit ?? prev.limit,
      requestedView: next.requestedView === undefined ? prev.requestedView : next.requestedView,
      prepend: next.prepend === true,
    }));
  }, []);
  const evidenceRef = useRef(evidence);
  evidenceRef.current = evidence;
  const readyLocatorKeyRef = useRef('');
  const pageKey = `${page.offset}:${page.limit ?? ''}:${page.requestedView ?? ''}:${page.prepend ? 1 : 0}`;

  useEffect(() => {
    const current = evidenceRef.current;
    if (!shouldHydrate || !current) {
      readyLocatorKeyRef.current = '';
      setState(IDLE);
      return;
    }
    if (!cellId || !accessToken || current.mobileAcquisitionId == null) {
      readyLocatorKeyRef.current = '';
      setState({
        ...IDLE,
        graphqlType: graphqlTypeOf(current),
        viewerHref: viewerHrefOf(current),
        status: 'error',
        errorMessage: MOBILE_USER_FACING_ERROR_MESSAGE,
      });
      return;
    }

    const controller = new AbortController();
    const requestKey = `${locatorKey}:${pageKey}`;
    // Token refresh and Table/Map toggles re-run this effect. Keep the ready
    // rows on screen so the pane is not replaced by a spinner and remounted.
    if (!hydrateKeepsCurrentRows(readyLocatorKeyRef.current, locatorKey)) {
      setState((currentState) => ({
        ...IDLE,
        status: 'loading',
        viewerHref: currentState.viewerHref,
        graphqlType: currentState.graphqlType,
        setPage,
      }));
    }

    const requestedView = page.requestedView || undefined;
    hydrateEvidence({
      cellId,
      accessToken,
      locator: {
        ...current,
        kind: current.kind,
        mobileAcquisitionId: current.mobileAcquisitionId,
        ...(requestedView ? { requestedView } : {}),
      },
      offset: page.offset,
      limit: requestedView === 'map' ? HYDRATE_MAP_POINT_CAP : (page.limit ?? HYDRATE_TABLE_PAGE_SIZE),
      signal: controller.signal,
    })
      .then((response) => {
        if (!shouldCommitHydrate(controller.signal)) return;
        readyLocatorKeyRef.current = requestKey;
        setState((currentState) => {
          const items = mergeHydratedWindow(currentState.items, response.items, page.prepend === true);
          const item = pickHydratedArtifactItem(items, response.focus);
          return {
            item,
            items,
            displayFields: displayFieldsOf(response.locator),
            focusIds: hydratedFocusIds(response.focus),
            highlightIds: hydratedFocusIds({
              mobileArtifactIds: response.focus?.highlightedArtifactIds,
            }),
            window: response.window ?? null,
            graphqlType: graphqlTypeOf(response.locator),
            view: typeof response.view === 'string' && response.view ? response.view : null,
            viewerHref: viewerHrefOf(current, response),
            display: hydrateDisplay(response.display),
            total: response.total,
            truncated: response.truncated,
            offset: page.prepend === true ? Math.min(currentState.offset, page.offset) : page.offset,
            limit: page.limit && page.limit > 0 ? page.limit : HYDRATE_TABLE_PAGE_SIZE,
            status: item ? 'ready' : 'empty',
            errorMessage: null,
            setPage,
          };
        });
      })
      .catch((error: unknown) => {
        if (!shouldCommitHydrate(controller.signal)) return;
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        readyLocatorKeyRef.current = '';
        setState({
          ...IDLE,
          graphqlType: graphqlTypeOf(current),
          viewerHref: viewerHrefOf(current),
          status: 'error',
          errorMessage: citationHydrateErrorMessage(error),
          setPage,
        });
      });

    return () => controller.abort();
  }, [shouldHydrate, locatorKey, pageKey, page.offset, page.limit, page.requestedView, cellId, accessToken]);

  return { ...state, limit: page.limit && page.limit > 0 ? page.limit : HYDRATE_TABLE_PAGE_SIZE };
}
